/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Domains (strategy 9.3, mockup admin-8, "Build as drawn"): one scorecard per domain from GET
 * /v1/admin/pulse/domains/:domain, Gyro AI for Revit first. The five tiles (performed, said it couldn't, tried and
 * failed, proposed not confirmed, and the PCs' MCP roll-up for the two MCP domains), the feedback split with its tags,
 * why it couldn't, the top intents with their trend, and the tools with their health. `?domain=` picks the domain.
 */
import { useState } from 'react';
import { CircleSlash, Wrench } from 'lucide-react';
import { api } from '../../lib/api';
import {
  countRows, domainLabel, healthChip, msText, pctText, pulseExportHref, pulseHref, reasonLabel, shareOf, sinceText, sortBy, subjectName, toggleSort, topErrorText,
  type PulseDomain, type PulseDomainScorecard, type SortState,
} from '../../lib/pulse';
import { navigate } from '../../shell/router';
import { EmptyState, Grid, Kpi, PageHeader, Panel, Skeleton, Sparkline, Stack, StatusChip, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { BarRow, DomainSelect, ExportButton, PulseError, RangeSelect, useLoad, useQueryParam } from './common';

const DEFAULT_DOMAIN: PulseDomain = 'gyro-ai-for-revit';
type IntentRow = PulseDomainScorecard['intents'][number];
type ToolRow = PulseDomainScorecard['tools'][number];

/** The first tile's name: what "done" means in the domain. */
function performedLabel(domain: string): string {
  if (domain === 'gyro-ai-for-revit') return 'Performed in Revit';
  if (domain === 'bluebeam-revu') return 'Performed in Revu';
  if (domain === 'chat' || domain === 'help' || domain === 'knowledge') return 'Answered';
  return 'Done';
}

function questionFor(domain: string): string {
  if (domain === 'gyro-ai-for-revit') return "Did Gyro perform the action in Revit, or say it couldn't?";
  if (domain === 'bluebeam-revu') return "Did Gyro perform the action in Revu, or say it couldn't?";
  if (domain === 'chat' || domain === 'help' || domain === 'knowledge') return "Did Gyro answer, or say it couldn't?";
  return "Did Gyro do what was asked, or say it couldn't?";
}

export function PulseDomains() {
  const [domainParam, setDomainParam] = useQueryParam('domain');
  const domain = domainParam || DEFAULT_DOMAIN;
  const [days, setDays] = useState(30);
  const [intentSort, setIntentSort] = useState<SortState>({ key: 'asks', direction: 'desc' });
  const [toolSort, setToolSort] = useState<SortState>({ key: 'successPct', direction: 'asc' });
  const { load, reload, reloading } = useLoad(() => api.pulseDomain(domain, { days }), `${domain}|${days}`);
  const card = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';

  const done = (card?.outcomes.done ?? 0) + (domain === 'chat' || domain === 'help' || domain === 'knowledge' ? card?.outcomes.answered ?? 0 : 0);
  const proposed = card?.outcomes['proposed-not-confirmed'] ?? 0;
  const unknown = card?.outcomes.unknown ?? 0;
  const couldNot = card?.outcomes['could-not'] ?? 0;
  const reasons = countRows(card?.reasons);
  const topReason = reasons[0]?.count ?? 1;
  const tags = countRows(card?.feedback.tags);

  const intentColumns: Array<Column<IntentRow>> = [
    { key: 'intent', header: 'Intent', sortable: true, cell: (row) => row.intent },
    { key: 'asks', header: 'Asks', numeric: true, sortable: true, cell: (row) => count(row.asks) },
    { key: 'helpedPct', header: 'Helped', numeric: true, sortable: true, cell: (row) => pctText(row.helpedPct) },
    { key: 'trend', header: 'Trend', hideOnPhone: true, cell: (row) => <Sparkline values={row.trend} label={`${row.intent}: asks per day`} tone={row.helpedPct < 50 ? 'bad' : 'data'} /> },
  ];
  const toolColumns: Array<Column<ToolRow>> = [
    // A tool from another server than the domain's own carries its server as a tag (mark_up_pdf "Revu" on the Revit page);
    // the domain's own server ("revit" inside gyro-ai-for-revit, "revu" inside bluebeam-revu) needs none.
    {
      key: 'tool', header: 'Tool', sortable: true,
      cell: (row) => {
        const name = subjectName(row.subjectId);
        return (
          <span className="ui-cell-stack">
            <span><code>{name.name}</code>{name.server && !domain.includes(name.server.toLowerCase()) ? <Tag mono={false}>{name.server}</Tag> : null}</span>
            {row.topError ? <span className="ui-cell-sub pulse-top-error" title={row.topError.text}>{topErrorText(row.topError)}</span> : null}
          </span>
        );
      },
    },
    { key: 'successPct', header: 'Success', numeric: true, sortable: true, cell: (row) => pctText(row.successPct) },
    { key: 'p95Ms', header: 'p95', numeric: true, sortable: true, cell: (row) => msText(row.p95Ms) },
    { key: 'state', header: 'Health', sortable: true, cell: (row) => <StatusChip tone={healthChip(row.state).tone}>{healthChip(row.state).label}{(row.state === 'failing' || row.state === 'quarantined') && row.since ? ` ${sinceText(row.since)}` : ''}</StatusChip> },
  ];
  const intents = card ? sortBy(card.intents, intentSort, (row, key) => (key === 'intent' ? row.intent : key === 'helpedPct' ? row.helpedPct : row.asks)) : [];
  const tools = card ? sortBy(card.tools, toolSort, (row, key) => (key === 'tool' ? subjectName(row.subjectId).name : key === 'p95Ms' ? row.p95Ms : key === 'state' ? row.state : row.successPct)) : [];

  return (
    <>
      <PageHeader
        title={domainLabel(domain)}
        crumbs={['Pulse', 'Domains', domainLabel(domain)]}
        meta={<span>{card ? `${count(card.asks)} asks in ${days} days. ` : ''}{questionFor(domain)}</span>}
        actions={(
          <>
            <DomainSelect value={domain} all={false} onChange={(next) => setDomainParam(next)} />
            <RangeSelect value={days} onChange={setDays} />
            <ExportButton href={pulseExportHref('records', { days, domain })} />
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          {load.state === 'failed' ? <PulseError title={`The ${domainLabel(domain)} scorecard could not be loaded`} error={load.error} onRetry={reload} retrying={reloading} /> : null}
          <div className="pulse-kpis" role="group" aria-label={`${domainLabel(domain)} at a glance`}>
            <Kpi label={performedLabel(domain)} loading={loading} value={card ? pctText(shareOf(done, card.asks)) : '—'} sub={card ? <span className="ui-text-ok">{count(done)} asks</span> : undefined} />
            <Kpi label="Said it couldn't" loading={loading} value={card ? pctText(shareOf(card.saidCannot, card.asks)) : '—'} sub={card ? <span className="ui-text-warn">{count(card.saidCannot)} asks</span> : undefined} />
            <Kpi label="Tried and failed" loading={loading} value={card ? pctText(shareOf(card.triedFailed, card.asks)) : '—'} sub={card ? <span className="ui-text-bad">{count(card.triedFailed)} asks</span> : undefined} />
            <Kpi label="Proposed, not confirmed" loading={loading} value={card ? pctText(shareOf(proposed, card.asks)) : '—'} sub={card ? `${count(proposed)} asks` : undefined} />
            {card?.mcp || domain === 'gyro-ai-for-revit' || domain === 'bluebeam-revu' ? (
              <Kpi
                label={domain === 'bluebeam-revu' ? 'Revu servers reachable' : 'Revit servers reachable'} loading={loading}
                value={card?.mcp ? pctText(card.mcp.reachablePct) : '—'}
                sub={card?.mcp ? `of ${count(card.mcp.devices)} PCs today, 24 h roll-up` : 'no PC has reported today'}
              />
            ) : (
              <Kpi label="Unknown" loading={loading} value={card ? pctText(shareOf(unknown, card.asks)) : '—'} sub={card ? `${count(unknown)} asks the judge could not decide` : undefined} />
            )}
          </div>

          <Grid cols={2}>
            <Panel title="Feedback" description="What people said right after an action" actions={card ? <span className="ui-quiet">{count(card.feedback.cards)} cards</span> : null}>
              {loading ? <div className="pulse-bars" aria-busy="true"><Skeleton /><Skeleton /><Skeleton /></div> : card && card.feedback.cards > 0 ? (
                <div className="ui-form">
                  <div className="pulse-bars">
                    <BarRow label="Worked" value={shareOf(card.feedback.worked, card.feedback.cards) / 100} figure={pctText(shareOf(card.feedback.worked, card.feedback.cards))} meterLabel="Worked" />
                    <BarRow label="Partly" value={shareOf(card.feedback.partly, card.feedback.cards) / 100} figure={pctText(shareOf(card.feedback.partly, card.feedback.cards))} meterLabel="Partly" />
                    <BarRow label="Didn't work" value={shareOf(card.feedback.failed, card.feedback.cards) / 100} figure={pctText(shareOf(card.feedback.failed, card.feedback.cards))} meterLabel="Didn't work" tone="bad" />
                    <BarRow label="Had to undo it" value={shareOf(card.feedback.undo, card.feedback.cards) / 100} figure={count(card.feedback.undo)} meterLabel="Had to undo it" tone="bad" />
                  </div>
                  <p className="ui-para ui-quiet">Tags on "Didn't work": {tags.length ? <span className="ui-chips">{tags.map((tag) => <StatusChip key={tag.key} tone="neutral" plain>{tag.key} {count(tag.count)}</StatusChip>)}</span> : 'none yet'}</p>
                </div>
              ) : <EmptyState compact icon={CircleSlash} title="No feedback cards yet">Cards appear after a higher-profile action settles, when the pulse-cards flag is on.</EmptyState>}
            </Panel>
            <Panel title="Why it couldn't" description="The reasons behind could-not" actions={card ? <span className="ui-quiet">{count(couldNot)} asks</span> : null}>
              {loading ? <div className="pulse-bars" aria-busy="true"><Skeleton /><Skeleton /><Skeleton /></div> : reasons.length ? (
                <div className="pulse-bars">
                  {reasons.map((row) => <BarRow key={row.key} label={reasonLabel(row.key)} value={row.count / topReason} figure={count(row.count)} meterLabel={`${reasonLabel(row.key)}: ${count(row.count)} asks`} />)}
                </div>
              ) : <EmptyState compact icon={CircleSlash} title="Nothing Gyro could not do">Reasons appear here when Gyro says it cannot, or tries and fails.</EmptyState>}
            </Panel>
          </Grid>

          <Grid cols={2}>
            <Panel flush title="What people ask" description="The intents in this domain" actions={<span className="ui-quiet">top intents</span>}>
              <Table
                label="What people ask"
                columns={intentColumns}
                rows={intents}
                rowKey={(row) => row.intentKey}
                loading={loading}
                skeletonRows={5}
                onRowClick={(row) => navigate(pulseHref.intent(row.intentKey))}
                sort={{ key: intentSort.key, direction: intentSort.direction, onSort: (key) => setIntentSort(toggleSort(intentSort, key, key !== 'intent')) }}
                empty={<EmptyState compact icon={CircleSlash} title="No asks in this range">Widen the date range.</EmptyState>}
              />
            </Panel>
            <Panel flush title="Tools" description="The tools this domain's asks used" actions={<span className="ui-quiet">success · p95 · health</span>}>
              <Table
                label="Tools"
                columns={toolColumns}
                rows={tools}
                rowKey={(row) => row.subjectId}
                loading={loading}
                skeletonRows={5}
                onRowClick={(row) => navigate(pulseHref.subject(row.subjectId))}
                sort={{ key: toolSort.key, direction: toolSort.direction, onSort: (key) => setToolSort(toggleSort(toolSort, key, key !== 'tool' && key !== 'state')) }}
                empty={<EmptyState compact icon={Wrench} title="No tools used">Asks in this domain were answered without a tool.</EmptyState>}
              />
            </Panel>
          </Grid>
        </Stack>
      </div>
    </>
  );
}
